// src/app/templates/Header/Header.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '@center-pwa/store/store';
import { setSphereId } from '@center-pwa/reducers/sphereReducer';
import { RootState } from '@center-pwa/store/store';
import {
  setAvailableContexts,
  extractLevel0Filters,
  extractAllLevelFilters,
} from '@center-pwa/reducers/activeContextReducer';
import { selectAllMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import logo from '../../../assets/images/center-icon.png';
import './Header.scss';
import {
  MetasphereNavBar,
  useApiClientsOrNull,
  useAvailableContexts,
  GlobalMetaspaces,
  useMetaspaceEvents,
  StatusBadgeAtom,
} from 'design-system/my-lib';
import { useTranslation } from 'localization';
import { useNavigate } from 'react-router-dom';
import type { Sphere } from 'types/global';
import type { ContextOption } from 'design-system/my-lib';

interface HeaderProps {
  sphereId: string;
  defaultLanguage: string;
  metaspaceId?: string;
  sphere?: Sphere | null;
  /** Reports which sphere the header is fully populated for (identity + breadcrumb),
   *  or null. MetaspacePage holds the metaspace back until this matches the sphere it
   *  is about to render, so the page fills in header-first instead of the metaspace
   *  beating the chrome to the screen. */
  onReadyChange?: (readySphereId: string | null) => void;
}

const Header: React.FC<HeaderProps> = ({
  sphereId,
  defaultLanguage,
  metaspaceId,
  sphere,
  onReadyChange,
}) => {
  const apiClients = useApiClientsOrNull();
  const { t, i18n } = useTranslation();
  const { emitOpenSphereEditor } = useMetaspaceEvents();
  const metaspaces = useSelector(selectAllMetaspaces);
  const globalMetaspace = metaspaces.find(
    (m) => m.metaspace_id === '00000000-0000-0000-0000-000000000001'
  );
  const selectedMetaspaceId = metaspaceId ?? globalMetaspace?.metaspace_id;

  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Fetch sphere-scoped contexts from deployed approaches
  const {
    contexts: rawContexts,
    loading: contextLoading,
  } = useAvailableContexts(apiClients, sphereId);

  // Sync to Redux for useContextUrlSync and useActiveContext consumers
  useEffect(() => {
    const summaries = rawContexts.map((ctx) => ({
      systemName: ctx.system_name,
      name: ctx.name,
      description: ctx.description,
      level0PropertyFilters: extractLevel0Filters(ctx),
      allLevelPropertyFilters: extractAllLevelFilters(ctx),
    }));
    dispatch(setAvailableContexts(summaries));
  }, [rawContexts, dispatch]);

  // Map Context[] to ContextOption[] for the presentational molecule
  const contextOptions: ContextOption[] = useMemo(
    () =>
      rawContexts.map((ctx) => ({
        systemName: ctx.system_name,
        name: ctx.name,
        description: ctx.description,
        level0PropertyFilters: extractLevel0Filters(ctx),
        allLevelPropertyFilters: extractAllLevelFilters(ctx),
      })),
    [rawContexts]
  );

  const handleLanguageChange = (newLang: string) => {
    setCurrentLanguage(newLang);
    i18n.changeLanguage(newLang);
  };

  const handleSphereChange = (newSphereId: string) => {
    dispatch(setSphereId({ sphereId: newSphereId, source: 'Header' }));
  };

  // Until the nav bar exists there is nothing loaded to report, so the header is ready
  // for no sphere. Once mounted, MetasphereNavBar owns the signal.
  const navMounted = !!apiClients && !!sphere;
  useEffect(() => {
    if (!navMounted) onReadyChange?.(null);
  }, [navMounted, onReadyChange]);

  const handleConfigureStatusFactors = (targetSphereId: string) => {
    // Emit event to open sphere editor with status factors tab
    emitOpenSphereEditor(targetSphereId, 'status-factors-tab');
  };

  const healthState = sphere?.properties?.healthState;
  const healthStateReason = sphere?.properties?.healthStateReason;

  const healthTone = healthState === 'unhealthy' ? 'danger'
    : healthState === 'degraded' ? 'warn'
    : null;

  const healthLabel = healthState === 'unhealthy' ? t('sphere.health.unhealthy')
    : healthState === 'degraded' ? t('sphere.health.degraded')
    : null;

  // Map known health-reason prefixes (e.g. `action_failed:abc-123`) to a
  // localized, user-facing label. Unknown reasons fall back to the generic
  // tooltip text — never expose the raw machine token to end users.
  const healthReasonLabel: string | null = (() => {
    if (!healthStateReason) return null;
    const prefix = healthStateReason.split(':')[0];
    switch (prefix) {
      case 'action_failed':
        return t('sphere.health.reason.action_failed');
      case 'manual':
        return t('sphere.health.reason.manual');
      default:
        return null;
    }
  })();

  const healthTooltip = healthReasonLabel
    ? `${t('sphere.health.tooltip')}: ${healthReasonLabel}`
    : t('sphere.health.tooltip');

  // The header shell (logo, title) renders unconditionally and reserves its height in
  // CSS. It used to return null until `sphere` arrived from MetaspacePage's fetch,
  // which meant the entire header vanished on every sphere/identity switch and then
  // popped back in, shoving the page content down. Only the sphere-dependent nav is
  // gated now, and it occupies a reserved slot while it waits.
  return (
    <header className="header">
      <div className="grid">
        <div className="col pr-1">
          <img
            src={logo}
            alt="Logo"
            className="logo"
            onClick={() => navigate('/')}
          />
        </div>
        <div className="col">
          <h1 className="header-title">{t('header.title')}</h1>
          {healthTone && healthLabel && (
            <StatusBadgeAtom
              tone={healthTone}
              label={healthLabel}
              icon={healthState === 'unhealthy' ? 'pi pi-times-circle' : 'pi pi-exclamation-triangle'}
              data-testid="sphere-health-badge"
              tooltip={healthTooltip}
            />
          )}
        </div>
        <div className="col pt-3 header-nav-slot">
          {navMounted && sphere && (
            <MetasphereNavBar
              sphere={sphere}
              onSphereChange={handleSphereChange}
              defaultLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
              metaspaceId={selectedMetaspaceId}
              onConfigureStatusFactors={handleConfigureStatusFactors}
              contexts={contextOptions}
              contextLoading={contextLoading}
              onReadyChange={onReadyChange}
            />
          )}
        </div>
        {apiClients && sphereId && (
          <GlobalMetaspaces
            sphereId={sphereId}
            metaspaceId={selectedMetaspaceId}
          />
        )}
      </div>
    </header>
  );
};

export default Header;
